import {
	SendMoneyPostValidationParameter,
	getSendMoneyPostValidationMappablePropertyNames,
} from "../view-models/SendMoneyPostValidationParameter";
import { useSessionStore } from "../../../state/src/useSessionStore";
import { ConsumerRegistration } from "../view-models/ConsumerRegistration";
import { ProductFieldInfoVisibility } from "../view-models/ProductFieldInfoVisibility";
import { toTwoLetterStateCode } from "./addressFieldValidation";

export function isOptProductFieldVisibility(v: unknown): boolean {
	return v === ProductFieldInfoVisibility.OPT || v === "OPT" || v === 1;
}

export function isReqProductFieldVisibility(v: unknown): boolean {
	return v === ProductFieldInfoVisibility.REQ || v === "REQ" || v === 0;
}

const LOWERCASE_TO_CANONICAL_SEND_VALIDATION_PROP: ReadonlyMap<string, string> = (() => {
	const m = new Map<string, string>();
	for (const name of getSendMoneyPostValidationMappablePropertyNames()) {
		m.set(name.toLowerCase(), name);
	}
	return m;
})();

function isKnownSendValidationProperty(lowerCaseKey: string): boolean {
	return LOWERCASE_TO_CANONICAL_SEND_VALIDATION_PROP.has(lowerCaseKey);
}

/** C# SetValuesForValidation skips assigning these tags via the generic string branch (handled elsewhere). */
const EXCLUDED_GFFP_TAGS_FROM_DIRECT_ASSIGN = new Set(["promocode", "formfreestaging", "cardswiped"]);

/**
 * Mirrors GffpHelper.SetValuesForValidation: xmlTag matching a DTO property (case-insensitive) → set that property;
 * otherwise non-empty values → FieldValues (like KeyValuePair xmlTag / fieldValue).
 */
export function applyStoreAdditionalFieldsToSendValidationRequest(
	request: SendMoneyPostValidationParameter,
	extras: Record<string, unknown> | undefined | null
): void {
	if (!extras || typeof extras !== "object") return;

	const req = request as unknown as Record<string, unknown>;

	for (const [rawKey, rawVal] of Object.entries(extras)) {
		const keyTrim = String(rawKey ?? "").trim();
		if (!keyTrim) continue;
		const keyLc = keyTrim.toLowerCase();

		if (keyLc === "promocode" || keyLc === "promocodes") {
			const v = rawVal === undefined || rawVal === null ? "" : String(rawVal).trim();
			if (v.length > 0 && !request.PromoCodeValues.includes(v)) {
				request.PromoCodeValues.push(v);
			}
			continue;
		}

		if (EXCLUDED_GFFP_TAGS_FROM_DIRECT_ASSIGN.has(keyLc)) {
			continue;
		}

		const canonical = LOWERCASE_TO_CANONICAL_SEND_VALIDATION_PROP.get(keyLc);
		if (canonical) {
			req[canonical] = rawVal === undefined ? undefined : rawVal;
			continue;
		}

		const strVal = rawVal === undefined || rawVal === null ? "" : String(rawVal).trim();
		if (strVal.length > 0) {
			request.FieldValues.push({ key: keyTrim, value: strVal });
		}
	}
}

/** Match GFFP xmlTag to an existing request key (case-insensitive), or default to Pascal-cased tag for new keys. */
function resolveValidationRequestKey(request: Record<string, unknown>, xmlTag: string): string {
	const tag = xmlTag.trim();
	if (!tag) return tag;
	const keys = Object.keys(request);
	const tagLc = tag.toLowerCase();
	const existing = keys.find((k) => k.toLowerCase() === tagLc);
	if (existing) return existing;
	const pascal = tag.charAt(0).toUpperCase() + tag.slice(1);
	const pascalHit = keys.find((k) => k.toLowerCase() === pascal.toLowerCase());
	return pascalHit ?? pascal;
}

/**
 * Send validation expects OPT fields from getFieldsForProduct to appear as empty XML elements when omitted.
 * JSON omits `undefined`; use empty string so keys are serialized.
 */
function applyEmptyOptionalGffpFields(
	request: SendMoneyPostValidationParameter,
	gffpFieldsByCategory: Record<string, any[]> | undefined | null
): void {
	if (!gffpFieldsByCategory || typeof gffpFieldsByCategory !== "object") return;
	const req = request as unknown as Record<string, unknown>;
	for (const category of Object.values(gffpFieldsByCategory)) {
		if (!Array.isArray(category)) continue;
		for (const field of category) {
			const vis = field?.visibility ?? field?.Visibility;
			if (!isOptProductFieldVisibility(vis)) continue;
			const rawTag = String(field?.xmlTag ?? field?.XmlTag ?? "").trim();
			if (!rawTag) continue;
			const key = resolveValidationRequestKey(req, rawTag);
			// Only set empty string on real DTO properties (Consumer Connect empty elements). Skip unknown tags like ACCOUNTTYPE.
			if (!isKnownSendValidationProperty(key.toLowerCase())) continue;
			const cur = req[key];
			if (cur !== undefined && cur !== null) continue;
			req[key] = "";
		}
	}
}

/**
 * GFFP may list customerReceiveNumber (REQ/OPT) but send UI does not collect it; when present in GFFP,
 * force empty DTO value and drop matching FieldValues so validation matches the hidden step behavior.
 */
const SEND_FLOW_UI_SKIPPED_GFFP_TAGS_LC = new Set(["customerreceivenumber"]);

function applySendFlowUiSkippedGffpFields(
	request: SendMoneyPostValidationParameter,
	gffpFieldsByCategory: Record<string, any[]> | undefined | null
): void {
	if (!gffpFieldsByCategory || typeof gffpFieldsByCategory !== "object") return;
	let shouldClearReceiveReg = false;
	for (const category of Object.values(gffpFieldsByCategory)) {
		if (!Array.isArray(category)) continue;
		for (const field of category) {
			const tagLc = String(field?.xmlTag ?? field?.XmlTag ?? "").trim().toLowerCase();
			if (SEND_FLOW_UI_SKIPPED_GFFP_TAGS_LC.has(tagLc)) {
				shouldClearReceiveReg = true;
				break;
			}
		}
		if (shouldClearReceiveReg) break;
	}
	if (!shouldClearReceiveReg) return;
	const req = request as unknown as Record<string, unknown>;
	const key = resolveValidationRequestKey(req, "customerReceiveNumber");
	if (isKnownSendValidationProperty(key.toLowerCase())) {
		req[key] = "";
	}
	request.FieldValues = (request.FieldValues || []).filter(
		(e) => String(e?.key ?? "").trim().toLowerCase() !== "customerreceivenumber"
	);
}

export const buildSendValidationRequestFromStore = (consumerId: string | number): SendMoneyPostValidationParameter => {
	const state = useSessionStore.getState() as any;

	const request = new SendMoneyPostValidationParameter();

	// BasicRequest fields
	request.agentID = state.agentId || undefined;
	request.unitProfileID = state.unitProfileId || undefined;

	// Core transaction amounts/currencies/options
	const opt = state.selectedSendOption || state.pendingSendOption || {};
	const amountFromState = Number(String(state.currentSendAmount || "0").replace(/,/g, ""));
	const amountFromOption = Number(opt?.SendAmounts?.SendAmount ?? 0);
	request.Amount = isFinite(amountFromOption) && amountFromOption > 0 ? amountFromOption : (isFinite(amountFromState) ? amountFromState : undefined);
	request.SendCurrency = opt?.SendAmounts?.SendCurrency || "USD";
	request.DestinationCountry = state.selectedDestinationCountryCode  || undefined;
	if(request.DestinationCountry?.toUpperCase() === "USA" || request.DestinationCountry?.toUpperCase() === "CAN") {
		request.DestinationState = state.selectedDestinationStateAbbr || undefined;
	}
	request.DeliveryOption = opt?.DeliveryOption || undefined;
	request.ReceiveCurrency = state.selectedReceiveCurrency || opt?.ValidReceiveCurrency || opt?.EstimatedReceiveCurrency || undefined;
	request.ReceiveAgentId = opt?.ReceiveAgentId ?? opt?.receiveAgentID ?? opt?.ReceiveAgentID ?? undefined;

	// Receipt language preferences
	request.PrimaryReceiptLanguage = "ENG";
	request.SecondaryReceiptLanguage = "SPA";

	// Consumer linkage
	request.ConsumerId = consumerId != null ? String(consumerId) : undefined;

	// MGI session id from selected service options or store fallback
	request.MgiTransactionSessionId = opt?.MgiTransactionSessionId || opt?.mgiTransactionSessionId || state.mgiTransactionSessionId || undefined;

	// Sender info from store
	request.SenderFirstName = state.senderFirstName || undefined;
	request.SenderMiddleName = state.senderMiddleName || undefined;
	request.SenderLastName = state.senderLastName || undefined;
	request.SenderLastName2 = state.senderLastName2 || undefined;
	request.SenderAddress = state.senderAddress || undefined;
	request.SenderCity = state.senderCity || undefined;
	request.SenderCountry = state.senderCountry || 'USA';
	request.FormFreeStaging = true;
	request.SenderZipCode = state.senderZipCode || undefined;
	// API accepts only 2-letter senderState; omit full region names / empty.
	request.SenderState = toTwoLetterStateCode(state.senderState) || undefined;
	request.SenderDOB = state.senderDOB || undefined;
	// Fallback: parse senderDOBString (MM/DD/YYYY)
	// if (!request.SenderDOB && state.senderDOBString) {
	// 	try {
	// 		const parts = String(state.senderDOBString).split("/");
	// 		if (parts.length === 3) {
	// 			const mm = parseInt(parts[0], 10);
	// 			const dd = parseInt(parts[1], 10);
	// 			const yyyy = parseInt(parts[2], 10);
	// 			const d = new Date(yyyy, mm - 1, dd);
	// 			if (!isNaN(d.getTime())) {
	// 				request.SenderDOB = d;
	// 			}
	// 		}
	// 	} catch {}
	// }
	if (!request.SenderDOB && state.senderDOBString) {
		const parts = state.senderDOBString.split("/");
		if (parts.length === 3) {
			const mm = Number(parts[0]);
			const dd = Number(parts[1]);
			const yyyy = Number(parts[2]);

			if (!Number.isNaN(mm) && !Number.isNaN(dd) && !Number.isNaN(yyyy)) {
				// Create date in local time at noon to avoid timezone boundary issues
				// This ensures the date doesn't shift when serialized to JSON
				request.SenderDOB = new Date(yyyy, mm - 1, dd, 12, 0, 0, 0);
			}
		}
	}
	request.SenderPhotoIdNumber = state.senderPhotoIdNumber || undefined;
	request.SenderPhotoIdCountry = state.selectedIdPhotoIdCountry || undefined;
	// Optional: ID type if chosen (store uses selectedIdTypeValue)
	request.SenderPhotoIdType = state.selectedIdTypeValue || undefined;
	request.SenderPhotoIdState = state.selectedIdPhotoIdStateAbbr || undefined;
	request.SenderHomePhone = state.phoneNumber || undefined;

	// Receiver info from store (captured in ReceiverFullNameStep)
	request.ReceiverFirstName = state.receiverFirstName || undefined;
	request.ReceiverMiddleName = state.receiverMiddleName || undefined;
	request.ReceiverLastName = state.receiverLastName || undefined;
	request.ReceiverLastName2 = state.receiverLastName2 || undefined;

	// If CustomerReceiveNumber captured in flow, include it
	request.CustomerReceiveNumber = state.customerReceiveNumber || undefined;

	// Include promo codes from the most recent FeeLookup params stored in session
	try {
		const promoCodes: string[] | undefined = state?.sendFeeLookupReviewEditStore?.promoCodes;
		if (Array.isArray(promoCodes) && promoCodes.length > 0) {
			request.PromoCodeValues = promoCodes.filter((p) => typeof p === 'string' && p.trim().length > 0);
		}
	} catch {}

	// ConsumerRegistration info: phone number and PIN
	const phone = state.phoneNumber || undefined;
	request.SenderHomePhone = phone;
	const pin = (() => {
		// Use a single canonical store field for PIN
		const p = state.consumerPin ?? state.pin ?? state.PIN ?? undefined;
		return p !== undefined && p !== null ? String(p) : undefined;
	})();
	if (phone || pin) {
		const reg = new ConsumerRegistration();
		// Preferred shape for API
		reg.consumerLoginID = phone;
		reg.consumerPIN = pin ;
		request.ConsumerRegistration = reg;
	}

	// GFFP additional fields: known xmlTags → DTO properties; unmapped non-empty → FieldValues (C# SetValuesForValidation behavior)
	try {
		applyStoreAdditionalFieldsToSendValidationRequest(request, state.additionalFieldValues);
	} catch {}

	applyEmptyOptionalGffpFields(request, state.gffpFieldsByCategory);
	applySendFlowUiSkippedGffpFields(request, state.gffpFieldsByCategory); // Hide RRN step on UI. 

    return request;
};

export function hasSendAdditionalInformationToCollect(
  missingRequiredFields: any[] | null | undefined,
): boolean {
  return (missingRequiredFields ?? []).some((field) =>
    isReqProductFieldVisibility(field?.visibility ?? field?.Visibility),
  );
}

export function getSendAdditionalFieldXmlTags(
  missingRequiredFields: any[] | null | undefined,
): Set<string> {
  return new Set(
    (missingRequiredFields || [])
      .map((f) => String(f?.XmlTag ?? f?.xmlTag ?? "").trim())
      .filter(Boolean),
  );
}

/** Drop additional-field store values that are no longer required for the current delivery option. */
export function pruneSendAdditionalFieldValuesForMissingFields(
  missingRequiredFields: any[] | null | undefined,
): void {
  const state = useSessionStore.getState() as any;
  const setAdditionalFieldValues = state.setAdditionalFieldValues as
    | ((values: Record<string, any>) => void)
    | undefined;
  if (!setAdditionalFieldValues) return;

  const allowedTags = getSendAdditionalFieldXmlTags(missingRequiredFields);
  const current = (state.additionalFieldValues || {}) as Record<string, any>;
  if (allowedTags.size === 0) {
    if (Object.keys(current).length > 0) {
      setAdditionalFieldValues({});
    }
    return;
  }

  const next: Record<string, any> = {};
  for (const [key, value] of Object.entries(current)) {
    if (allowedTags.has(key)) next[key] = value;
  }
  setAdditionalFieldValues(next);
}

export function buildSendAdditionalInfoDisplayText(
  missingRequiredFields: any[] | null | undefined,
  additionalFieldValues: Record<string, any> | null | undefined,
): string {
  const allowedTags = getSendAdditionalFieldXmlTags(missingRequiredFields);
  return Object.entries(additionalFieldValues || {})
    .filter(([key, value]) => allowedTags.has(key) && value)
    .map(([, value]) => value)
    .join(", ");
}

/** Turn API field names like senderPhotoIdState into "Sender photo id state". */
export function humanizeOffendingFieldTag(tag: string): string {
	const t = String(tag ?? "").trim();
	if (!t) return "";
	const spaced = t.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
	const lower = spaced.toLowerCase();
	return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** User-facing label for GFFP / additional-information fields (send, bill pay, receive). */
export function getProductFieldDisplayLabel(field: any, xmlTag: string): string {
	const key = String(xmlTag ?? "").trim();
	const fromApi = String(field?.FieldLabel ?? field?.fieldLabel ?? "").trim();
	if (fromApi) return fromApi;
	return humanizeOffendingFieldTag(key) || key;
}

/**
 * Unwrap validateConsumer responses: nested data, stringified JSON, or ErrorDetail as array.
 */
export function extractErrorDetailFromValidateConsumerResponse(validationResponse: unknown): any {
	let raw: any =
		validationResponse &&
		typeof validationResponse === "object" &&
		(validationResponse as any).data !== undefined
			? (validationResponse as any).data
			: validationResponse;
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw);
		} catch {
			return null;
		}
	}
	if (!raw || typeof raw !== "object") return null;
	let errDetail: any = raw.ErrorDetail ?? raw.errorDetail;
	if (Array.isArray(errDetail)) errDetail = errDetail[0];
	return errDetail ?? null;
}

/** User-visible message including OffendingField when Consumer Connect returns it. */
export function formatValidateConsumerErrorMessage(errDetail: any): string {
	if (!errDetail) return "An error occurred.";
	const errCode = errDetail?.ErrorCode ?? errDetail?.errorCode ?? "UNKNOWN";
	const errString = errDetail?.ErrorString ?? errDetail?.errorString ?? "An error occurred.";
	const offendingRaw = String(errDetail?.OffendingField ?? errDetail?.offendingField ?? "").trim();
	let msg = `${errCode} ${errString}`.trim();
	if (offendingRaw) {
		const friendly = humanizeOffendingFieldTag(offendingRaw) || offendingRaw;
		const friendlyNorm = friendly.replace(/\s/g, "").toLowerCase();
		const rawNorm = offendingRaw.replace(/_/g, "").toLowerCase();
		msg += " — Field: ";
		msg += friendlyNorm === rawNorm ? friendly : `${friendly} (${offendingRaw})`;
	}
	return msg;
}

export function parseBffResponseText(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}

/** Prefer ErrorDetail.ErrorString for user-facing validation errors. */
export function getValidationErrorMessageFromResponse(
	responseBody: unknown,
	fallbackMessage = "Validation failed.",
): string {
	const errDetail = extractErrorDetailFromValidateConsumerResponse(responseBody);
	if (errDetail) {
		const errString = String(
			errDetail?.ErrorString ?? errDetail?.errorString ?? "",
		).trim();
		if (errString) return errString;
	}
	return fallbackMessage;
}
